import { Injectable } from '@angular/core';
import { combineQueries, filterNilValue, Order, QueryEntity } from '@datorama/akita';
import { BooksService } from './books.service';
import { BooksState, BooksStore } from './books.store';
import { LegalEntity } from '../../../../pages/admin/state/legal-entities/legal-entity.model';
import { LegalEntityQuery } from '../../../../pages/admin/state/legal-entities/legal-entity.query';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { Toast, ToastService } from 'src/app/shared/services/toast/toast.service';

@Injectable({ providedIn: 'root' })
export class BooksQuery extends QueryEntity<BooksState> {
    constructor(
        protected store: BooksStore,
        private booksService: BooksService,
        private _ownersQuery: LegalEntityQuery,
        private _toastService: ToastService
    ) {
        super(store);
        this.booksService.get().subscribe(
            (books) => {},
            (error) => {
                let toastError: Toast;
                switch (error.error.status) {
                    case 404:
                        toastError = {
                            intent: 'critical',
                            title: 'Service not available',
                            message: 'The accounting service is not available.'
                        };
                        break;
                    default:
                        toastError = {
                            intent: 'critical',
                            message: `An unexpected error occurred (code ${error.error.status}). Please try again later.`
                        };
                }
                this._toastService.show(toastError);
            }
        );
    }

    isLoading$ = this.selectLoading();

    /** allBooks$ - Select all books */
    all$ = this.selectAll();

    hasAccountingBooks$ = this.selectLoading().pipe(
        filter((loading) => !loading),
        switchMap((_) =>
            this.selectCount().pipe(
                map((count) => count > 0),
                shareReplay(1)
            )
        )
    );

    /** activeBook$ - Select active book */
    activeBook$ = this.selectActive().pipe(filterNilValue());

    /** activeBook - Get active book */
    get activeBook() {
        return this.getActive();
    }

    /** selectByOwner$ - Select all books for owner sorted by country code
     * @param ownerId - The owner id to filter by
     */
    selectByOwner$(ownerId: LegalEntity['ownerId']) {
        return this.selectAll({
            sortByOrder: Order.ASC,
            sortBy: 'countryCode',
            filterBy: (book) => +book.ownerId === ownerId
        });
    }

    selectAllWithOwner$() {
        return combineQueries([
            this.selectAll(),
            this._ownersQuery.selectLoading().pipe(
                filter((isLoading) => !isLoading),
                switchMap((_) => this._ownersQuery.selectAll())
            )
        ]).pipe(
            map(([books, owners]) => {
                return books.map((book) => {
                    const owner = owners.find((owner) => owner.ownerId === book.ownerId);
                    return {
                        ...book,
                        ownerName: owner?.longName || owner?.shortName
                    };
                });
            })
        );
    }
}
